import React from 'react';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { Box, Stack, useTheme } from '@mui/material';
import { ThinkingContent } from 'app/concierge/types';
import useBundleTranslation from 'i18n';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-cloud9_day';
import { renderMarkdown } from 'tools/safeHtml';

interface IProps {
    show: boolean;
    onHide: () => void;
    thinkingContent: ThinkingContent;
}

export default function ThinkingPopup({ show, onHide, thinkingContent }: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const appTheme = useTheme();

    const settings: PopupSettings = {
        title: t('thinking.title'),
        noButtons: true,
    };

    return show ? (
        <Popup maxWidth={'popupLg'} settings={settings} open={show} onHide={onHide}>
            <Stack spacing={2}>
                {thinkingContent.content.map((item, index) => (
                    <Box key={index}>
                        {item.label && <Box sx={{ fontWeight: 'bold', mb: 1 }}>{item.label}</Box>}
                        {item.type === 'sql' ? (
                            <AceEditor
                                mode="sql"
                                theme="cloud9_day"
                                name={`thinking-sql-${index}`}
                                value={item.value}
                                readOnly={true}
                                fontSize={appTheme.typography.fontSize}
                                showPrintMargin={false}
                                showGutter={true}
                                highlightActiveLine={false}
                                width="100%"
                                minLines={3}
                                maxLines={40}
                                setOptions={{
                                    showLineNumbers: true,
                                    tabSize: 2,
                                    fontFamily: appTheme.font.monospace,
                                    readOnly: true,
                                    wrap: true,
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    '& p': { m: 0, mb: 0.5 },
                                    '& h1, & h2, & h3, & h4, & h5, & h6': { m: 0, mb: 0.5, mt: 1 },
                                    '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 },
                                    '& ul, & ol': { m: 0, pl: 2, mb: 0.5 },
                                    '& li': { mb: 0.25 },
                                    '& blockquote': {
                                        m: 0,
                                        ml: 1,
                                        pl: 1,
                                        borderLeft: '2px solid',
                                        borderColor: 'divider',
                                    },
                                }}
                                dangerouslySetInnerHTML={{ __html: renderMarkdown(item.value) }}
                            />
                        )}
                    </Box>
                ))}
            </Stack>
        </Popup>
    ) : null;
}
